import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PROJECT_MAX_FILE_SIZE_BYTES, type ProjectEntry } from '@erp/rpc';
import { useGT } from 'gt-react';
import {
  uploadProjectFile,
  type ProjectUploadProgress,
} from './projectUploads';

const MAX_CONCURRENT_FILES = 3;

export type ProjectUploadSource = 'workspace' | 'nami';
export type ProjectUploadStatus =
  'queued' | 'uploading' | 'finalizing' | 'succeeded' | 'failed' | 'cancelled';

export interface ProjectUploadTask {
  id: string;
  projectId: string;
  parentEntryId: string | null;
  source: ProjectUploadSource;
  file: File;
  status: ProjectUploadStatus;
  loadedBytes: number;
  totalBytes: number;
  entry?: ProjectEntry;
  errorMessage?: string;
}

interface UseProjectUploadQueueOptions {
  projectId: string;
  onSuccess: (task: ProjectUploadTask, entry: ProjectEntry) => void;
}

interface EnqueueOptions {
  files: File[];
  parentEntryId: string | null;
  source: ProjectUploadSource;
}

function isActive(status: ProjectUploadStatus): boolean {
  return (
    status === 'queued' || status === 'uploading' || status === 'finalizing'
  );
}

function progressPatch(
  progress: ProjectUploadProgress,
): Pick<ProjectUploadTask, 'status' | 'loadedBytes' | 'totalBytes'> {
  return {
    status: progress.phase === 'finalizing' ? 'finalizing' : 'uploading',
    loadedBytes: progress.loadedBytes,
    totalBytes: progress.totalBytes,
  };
}

// Keep the tested upload lifecycle together; splitting its callbacks obscures shared queue state.
// fallow-ignore-next-line complexity
export function useProjectUploadQueue({
  projectId,
  onSuccess,
}: UseProjectUploadQueueOptions) {
  const gt = useGT();
  const [tasks, setTasks] = useState<ProjectUploadTask[]>([]);
  const tasksRef = useRef<ProjectUploadTask[]>([]);
  const waitingRef = useRef<string[]>([]);
  const runningRef = useRef(new Set<string>());
  const controllersRef = useRef(new Map<string, AbortController>());
  const pumpRef = useRef<() => void>(() => undefined);
  const successRef = useRef(onSuccess);

  useEffect(() => {
    successRef.current = onSuccess;
  }, [onSuccess]);

  const replaceTasks = useCallback((next: ProjectUploadTask[]) => {
    tasksRef.current = next;
    setTasks(next);
  }, []);

  const patchTask = useCallback(
    (taskId: string, patch: Partial<ProjectUploadTask>) => {
      replaceTasks(
        tasksRef.current.map((task) =>
          task.id === taskId ? { ...task, ...patch } : task,
        ),
      );
    },
    [replaceTasks],
  );

  const runTask = useCallback(
    (task: ProjectUploadTask) => {
      const controller = new AbortController();
      controllersRef.current.set(task.id, controller);
      runningRef.current.add(task.id);
      patchTask(task.id, { status: 'uploading' });

      void uploadProjectFile(task.projectId, task.parentEntryId, task.file, {
        signal: controller.signal,
        onProgress: (progress) => patchTask(task.id, progressPatch(progress)),
      })
        .then((entry) => {
          patchTask(task.id, {
            status: 'succeeded',
            loadedBytes: task.file.size,
            entry,
            errorMessage: undefined,
          });
          successRef.current(task, entry);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            patchTask(task.id, { status: 'cancelled' });
            return;
          }
          patchTask(task.id, {
            status: 'failed',
            errorMessage:
              error instanceof Error
                ? error.message
                : gt('Upload failed. Try again.', {
                    context: 'fallback project file upload error',
                  }),
          });
        })
        .finally(() => {
          controllersRef.current.delete(task.id);
          runningRef.current.delete(task.id);
          pumpRef.current();
        });
    },
    [gt, patchTask],
  );

  const pump = useCallback(() => {
    while (runningRef.current.size < MAX_CONCURRENT_FILES) {
      const taskId = waitingRef.current.shift();
      if (!taskId) return;
      const task = tasksRef.current.find(
        (candidate) => candidate.id === taskId && candidate.status === 'queued',
      );
      if (task) runTask(task);
    }
  }, [runTask]);

  useEffect(() => {
    pumpRef.current = pump;
  }, [pump]);

  const enqueue = useCallback(
    ({ files, parentEntryId, source }: EnqueueOptions): string[] => {
      const nextTasks = files.map<ProjectUploadTask>((file) => ({
        id: crypto.randomUUID(),
        projectId,
        parentEntryId,
        source,
        file,
        status: file.size > PROJECT_MAX_FILE_SIZE_BYTES ? 'failed' : 'queued',
        loadedBytes: 0,
        totalBytes: file.size,
        ...(file.size > PROJECT_MAX_FILE_SIZE_BYTES
          ? {
              errorMessage: gt('Files must be 5 GiB or smaller.', {
                context: 'project file upload size validation error',
              }),
            }
          : {}),
      }));
      replaceTasks([...tasksRef.current, ...nextTasks]);
      waitingRef.current.push(
        ...nextTasks
          .filter((task) => task.status === 'queued')
          .map((task) => task.id),
      );
      pumpRef.current();
      return nextTasks.map((task) => task.id);
    },
    [gt, projectId, replaceTasks],
  );

  const cancel = useCallback(
    (taskId: string) => {
      const task = tasksRef.current.find(
        (candidate) => candidate.id === taskId,
      );
      if (!task || !isActive(task.status) || task.status === 'finalizing')
        return;
      if (task.status === 'queued') {
        waitingRef.current = waitingRef.current.filter((id) => id !== taskId);
        patchTask(taskId, { status: 'cancelled' });
        return;
      }
      controllersRef.current.get(taskId)?.abort();
    },
    [patchTask],
  );

  const retry = useCallback(
    (taskId: string) => {
      const task = tasksRef.current.find(
        (candidate) => candidate.id === taskId,
      );
      if (!task || task.status !== 'failed') return;
      if (task.file.size > PROJECT_MAX_FILE_SIZE_BYTES) return;
      patchTask(taskId, {
        status: 'queued',
        loadedBytes: 0,
        entry: undefined,
        errorMessage: undefined,
      });
      waitingRef.current.push(taskId);
      pumpRef.current();
    },
    [patchTask],
  );

  const cancelAll = useCallback(() => {
    for (const task of tasksRef.current) cancel(task.id);
  }, [cancel]);

  useEffect(
    () => () => {
      for (const controller of controllersRef.current.values()) {
        controller.abort();
      }
      waitingRef.current = [];
    },
    [],
  );

  return {
    tasks,
    enqueue,
    cancel,
    retry,
    cancelAll,
    hasActiveTasks: useMemo(
      () => tasks.some((task) => isActive(task.status)),
      [tasks],
    ),
  };
}
